import moment from "moment";
import { ContractStatusDto, type ContractReportQueryDto } from "../../autogen/bff-api";

export type ExpiryWindowKey = "month" | "quarter" | "halfYear" | "year";

export interface ExpiryWindow {
  key: ExpiryWindowKey;
  labelKey: string;
  /** Months from today the window ends at; it starts where the previous one left off. */
  endsAfterMonths: number;
}

/**
 * When agreements run out, in bands rather than on a timeline.
 *
 * The bands are cumulative in speech — "next month", "next quarter", "next half year" — but a chart
 * needs disjoint slices, so each one picks up where the last ended. Together they cover the coming
 * year, which is the horizon renegotiation actually needs.
 */
export const EXPIRY_WINDOWS: ExpiryWindow[] = [
  { key: "month", labelKey: "Within a month", endsAfterMonths: 1 },
  { key: "quarter", labelKey: "1–3 months", endsAfterMonths: 3 },
  { key: "halfYear", labelKey: "3–6 months", endsAfterMonths: 6 },
  { key: "year", labelKey: "6–12 months", endsAfterMonths: 12 },
];

/**
 * A contract report query for one band. `Active` keeps out agreements that already ended or were
 * terminated — an expiry overview is about what is still running and about to stop.
 */
export const expiryWindowQuery = (
  window: ExpiryWindow,
  organizationId: string,
  now: moment.Moment = moment()
): Partial<ContractReportQueryDto> => {
  const index = EXPIRY_WINDOWS.findIndex((candidate) => candidate.key === window.key);
  const previousMonths = index > 0 ? EXPIRY_WINDOWS[index - 1].endsAfterMonths : 0;
  const from = index === 0 ? now.clone().startOf("day") : now.clone().startOf("day").add(previousMonths, "months").add(1, "day");

  return {
    owningOrganizationId: organizationId,
    statuses: [ContractStatusDto.Active],
    endDateFrom: from.format("YYYY-MM-DD"),
    endDateTo: now.clone().startOf("day").add(window.endsAfterMonths, "months").format("YYYY-MM-DD"),
  };
};
